import { z } from 'zod';
import { MESSAGE_FIN_AVANT_DEBUT, finApresDebut, heureReelleSchema } from './comprehension.js';

// Les ROUTINES (spec du 2026-08-28) : la semaine type, saisie a la main.
//
// AUCUN appel d'intelligence artificielle sur ce chemin. C'est la porte d'entree
// principale de l'ecran « Ma semaine » — plus sure que la dictee pour une saisie
// repetitive, et gratuite.

/** Jours de la semaine, 0 = dimanche, comme `Date.getDay()` et `recurrenceDays`. */
export const joursSchema = z
  .array(z.number().int().min(0).max(6))
  .min(1, 'Choisis au moins un jour')
  .max(7)
  // Deux fois le meme jour produirait une serie incoherente : on deduplique ici plutot
  // que d'y penser a chaque appel.
  .transform((jours) => [...new Set(jours)].sort((a, b) => a - b));

export const creationRoutineSchema = z
  .object({
    title: z.string().trim().min(1, 'Donne un nom à cette routine').max(200, 'Le nom est trop long'),
    startTime: heureReelleSchema,
    /** null = sans heure de fin. Une routine sans duree occupe quand meme sa minute. */
    endTime: heureReelleSchema.nullable().default(null),
    days: joursSchema,
  })
  .refine((v) => finApresDebut(v.startTime, v.endTime), {
    message: MESSAGE_FIN_AVANT_DEBUT,
    path: ['endTime'],
  });

export const modificationRoutineSchema = z
  .object({
    title: z.string().trim().min(1, 'Donne un nom à cette routine').max(200).optional(),
    startTime: heureReelleSchema.optional(),
    endTime: heureReelleSchema.nullable().optional(),
    days: joursSchema.optional(),
  })
  .refine((valeur) => Object.keys(valeur).length > 0, { message: 'Rien à modifier' })
  // La creation refusait deja une fin avant le debut ; la modification, non. Le schema
  // juge les deux heures donnees ENSEMBLE ; quand une seule arrive, c'est la route qui
  // compare a ce qui est enregistre (audit du 2026-09-27).
  .refine(
    (valeur) =>
      valeur.startTime === undefined ||
      valeur.endTime === undefined ||
      finApresDebut(valeur.startTime, valeur.endTime),
    { message: MESSAGE_FIN_AVANT_DEBUT, path: ['endTime'] },
  );

export type CreationRoutine = z.infer<typeof creationRoutineSchema>;
export type ModificationRoutine = z.infer<typeof modificationRoutineSchema>;

/** Les noms des jours, dans l'ordre de `Date.getDay()`. Une seule liste pour tout le projet. */
export const NOMS_DES_JOURS = [
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
] as const;

/** L'ordre d'AFFICHAGE d'une semaine française : lundi d'abord, dimanche en dernier. */
export const SEMAINE_AFFICHEE = [1, 2, 3, 4, 5, 6, 0] as const;
